import type { CurrentUser } from '@zhiyue/core';
import { createAppRequest } from './request';

const OPEN_PLATFORM_TOKEN_STORAGE_KEY = 'comment.openPlatformAccessToken';
const TOKEN_REFRESH_BUFFER_MS = 60_000;
const request = createAppRequest();

export type OpenPlatformTokenResult = {
  accessToken: string;
  expiresAt: number;
};

type OpenPlatformTokenContext = Pick<
  CurrentUser,
  'tenantUuid' | 'memberUuid'
> & {
  organizationUuid?: string;
};

type CachedOpenPlatformToken = OpenPlatformTokenResult & {
  cacheKey: string;
};

const normalizeText = (value: unknown) => String(value ?? '').trim();

const getCacheKey = (currentUser: OpenPlatformTokenContext) =>
  [
    normalizeText(currentUser.tenantUuid),
    normalizeText(currentUser.organizationUuid),
    normalizeText(currentUser.memberUuid),
  ].join('|');

const readCachedToken = (cacheKey: string): OpenPlatformTokenResult | null => {
  try {
    const rawValue = sessionStorage.getItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
    if (!rawValue) return null;

    const value = JSON.parse(rawValue) as CachedOpenPlatformToken;
    if (
      value.cacheKey !== cacheKey ||
      typeof value.accessToken !== 'string' ||
      !value.accessToken.trim() ||
      typeof value.expiresAt !== 'number' ||
      value.expiresAt <= Date.now() + TOKEN_REFRESH_BUFFER_MS
    ) {
      sessionStorage.removeItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
      return null;
    }

    return { accessToken: value.accessToken, expiresAt: value.expiresAt };
  } catch {
    sessionStorage.removeItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
    return null;
  }
};

export const clearOpenPlatformToken = () => {
  sessionStorage.removeItem(OPEN_PLATFORM_TOKEN_STORAGE_KEY);
};

export const fetchOpenPlatformToken = async (
  currentUser: OpenPlatformTokenContext,
): Promise<OpenPlatformTokenResult> => {
  const cacheKey = getCacheKey(currentUser);
  if (
    !normalizeText(currentUser.tenantUuid) ||
    !normalizeText(currentUser.organizationUuid) ||
    !normalizeText(currentUser.memberUuid)
  ) {
    clearOpenPlatformToken();
    throw new Error('当前会话缺少成员目录所需信息');
  }

  const cachedToken = readCachedToken(cacheKey);
  if (cachedToken) return cachedToken;

  const result = await request<OpenPlatformTokenResult>(
    '/bff/open-platform/token',
    {
      method: 'GET',
    },
  );
  if (!result?.accessToken || typeof result.expiresAt !== 'number') {
    throw new Error('开放平台令牌响应无效');
  }

  sessionStorage.setItem(
    OPEN_PLATFORM_TOKEN_STORAGE_KEY,
    JSON.stringify({ ...result, cacheKey }),
  );
  return result;
};
